import React from "react";
import { getWsrvUrl, getSrcSet, PRESETS } from "@/utils/wsrv";

/**
 * Optimized Image via wsrv.nl CDN
 *
 * Automatically generates:
 * - webp output with fallback srcSet
 * - responsive srcSet for various screen sizes
 * - lazy loading (unless priority)
 * - async decoding
 * - fetchPriority hint for LCP
 *
 * Usage:
 * <WsrvImage src="https://i.filecdn.in/..." alt="..." width={600} height={400} sizes="(max-width: 768px) 100vw, 50vw" preset="card" priority />
 */
export default function WsrvImage({
  src,
  alt = "",
  width,
  height,
  sizes,
  widths,
  preset,
  quality = 80,
  fit,
  priority = false,
  className,
  style,
  // Allow forcing output format (webp/avif/auto)
  output = "webp",
  decoding = "async",
  // Pass through extra img props
  ...rest
}) {
  if (!src) return null;

  // Determine widths to use
  let resolvedWidths = widths;
  if (!resolvedWidths && preset && PRESETS[preset]) {
    resolvedWidths = PRESETS[preset];
  }
  if (!resolvedWidths) {
    // Default: if width prop known, generate around it; else responsive full-width
    if (width && typeof width === "number") {
      // Generate DPR variants + steps around width
      resolvedWidths = Array.from(new Set([width, width * 1.5, width * 2].map(Math.round)))
        .filter((w) => w <= 3840);
      // Fallback to preset if too small
      if (resolvedWidths.length < 3) resolvedWidths = PRESETS.card;
    } else {
      resolvedWidths = PRESETS.card;
    }
  }

  // Auto sizes if not provided
  let resolvedSizes = sizes;
  if (!resolvedSizes) {
    if (preset === "hero") resolvedSizes = "100vw";
    else if (preset === "thumb") resolvedSizes = "(max-width: 768px) 50vw, 320px";
    else if (preset === "logo") resolvedSizes = "128px";
    else if (width) resolvedSizes = `(max-width: 768px) 100vw, ${width}px`;
    else resolvedSizes = "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw";
  }

  // For hero / large images we want a big fallback src
  const maxW = Math.max(...resolvedWidths);
  const fallbackW = priority ? maxW : Math.min(maxW, 1200);

  const srcSet = getSrcSet(src, resolvedWidths, { q: quality, output, fit });
  const srcUrl = getWsrvUrl(src, { w: fallbackW, q: quality, output, fit, h: height && width ? undefined : undefined });

  // If wsrv is not applicable (local img), srcSet will be undefined - use original src
  const finalSrc = srcUrl || src;
  const finalSrcSet = srcSet;

  return (
    <img
      src={finalSrc}
      srcSet={finalSrcSet}
      sizes={finalSrcSet ? resolvedSizes : undefined}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? "eager" : "lazy"}
      decoding={decoding}
      fetchPriority={priority ? "high" : "auto"}
      className={className}
      style={style}
      {...rest}
    />
  );
}

/**
 * BackgroundImage helper - use for inline background styles
 * Returns style object with wsrv optimized url
 */
export function getWsrvBgStyle(src, width = 1920, opts = {}) {
  if (!src) return {};
  const url = getWsrvUrl(src, { w: width, ...opts });
  return { backgroundImage: `url('${url}')` };
}
